<template>
  <div class="location-card" :class="{ 'location-card--closed': isClosed }">
    <div class="location-card__header">
      <div class="location-card__name">{{ location.name }}</div>
      <div class="location-card__status" :class="statusClass">
        <span class="location-card__status-dot"></span>
        {{ statusText }}
      </div>
    </div>

    <div class="location-card__metrics">
      <div class="location-card__metric location-card__metric--primary">
        <span class="location-card__metric-value">{{ formattedSales }}</span>
        <span class="location-card__metric-label">{{ salesLabel }}</span>
        <span v-if="salesDate" class="location-card__metric-date">{{ salesDate }}</span>
        <span v-if="salesTrend !== null" class="location-card__trend" :class="trendClass">
          <font-awesome-icon :icon="trendIcon" />
          {{ Math.abs(salesTrend) }}%
        </span>
      </div>

      <div class="location-card__row">
        <div class="location-card__metric">
          <span class="location-card__metric-value">{{ ordersToday }}</span>
          <span class="location-card__metric-label">Ordrer</span>
        </div>
        <div class="location-card__metric">
          <span class="location-card__metric-value">{{ formattedAvgOrder }}</span>
          <span class="location-card__metric-label">Gj.snitt</span>
        </div>
        <div class="location-card__metric">
          <span class="location-card__metric-value">{{ peakHour || '--:--' }}</span>
          <span class="location-card__metric-label">Peak</span>
        </div>
      </div>
    </div>

    <div class="location-card__footer">
      <button class="location-card__btn" @click="$emit('view-details', location.id)">
        <font-awesome-icon icon="chart-line" class="me-1" />
        Se detaljer
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LocationOverviewCard',
  props: {
    location: {
      type: Object,
      required: true
    },
    salesToday: {
      type: Number,
      default: 0
    },
    salesLabel: {
      type: String,
      default: 'Dagens omsetning'
    },
    salesDate: {
      type: String,
      default: ''
    },
    salesYesterday: {
      type: Number,
      default: 0
    },
    ordersToday: {
      type: Number,
      default: 0
    },
    peakHour: {
      type: String,
      default: ''
    },
    isClosed: {
      type: Boolean,
      default: false
    }
  },
  emits: ['view-details'],
  computed: {
    formattedSales() {
      return new Intl.NumberFormat('nb-NO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(this.salesToday) + ' kr';
    },
    formattedAvgOrder() {
      if (!this.ordersToday || this.ordersToday === 0) return '0 kr';
      const avg = Math.round(this.salesToday / this.ordersToday);
      return new Intl.NumberFormat('nb-NO').format(avg) + ' kr';
    },
    salesTrend() {
      if (!this.salesYesterday || this.salesYesterday === 0) return null;
      return Math.round(((this.salesToday - this.salesYesterday) / this.salesYesterday) * 100);
    },
    trendClass() {
      if (this.salesTrend > 0) return 'location-card__trend--positive';
      if (this.salesTrend < 0) return 'location-card__trend--negative';
      return 'location-card__trend--neutral';
    },
    trendIcon() {
      if (this.salesTrend > 0) return 'arrow-up';
      if (this.salesTrend < 0) return 'arrow-down';
      return 'minus';
    },
    statusClass() {
      return this.isClosed ? 'location-card__status--closed' : 'location-card__status--open';
    },
    statusText() {
      return this.isClosed ? 'Stengt' : 'Åpen';
    }
  }
};
</script>

<style scoped>
.location-card {
  background: var(--bo-surface);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.location-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.location-card--closed {
  opacity: 0.7;
  background: var(--bo-surface-2);
}

.location-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.location-card__name {
  font-size: 16px;
  font-weight: 600;
  color: var(--bo-ink);
}

.location-card__status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 12px;
}

.location-card__status--open {
  background: var(--bo-ok-bg);
  color: var(--bo-success-ink);
}

.location-card__status--closed {
  background: var(--bo-bad-bg);
  color: var(--bo-alert-ink);
}

.location-card__status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}

.location-card__metrics {
  flex: 1;
}

.location-card__metric {
  display: flex;
  flex-direction: column;
}

.location-card__metric--primary {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--bo-surface-2);
  position: relative;
}

.location-card__metric-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--bo-ink);
}

.location-card__metric--primary .location-card__metric-value {
  font-size: 28px;
}

.location-card__metric-label {
  font-size: 12px;
  color: var(--bo-muted);
  margin-top: 2px;
}

.location-card__metric-date {
  font-size: 11px;
  color: var(--bo-muted-2);
  margin-top: 1px;
}

.location-card__row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.location-card__row .location-card__metric-value {
  font-size: 18px;
}

.location-card__trend {
  position: absolute;
  top: 0;
  right: 0;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.location-card__trend--positive {
  background-color: var(--bo-ok-bg);
  color: var(--bo-success-ink);
}

.location-card__trend--negative {
  background-color: var(--bo-bad-bg);
  color: var(--bo-alert-ink);
}

.location-card__trend--neutral {
  background-color: var(--bo-surface-2);
  color: var(--bo-muted);
}

.location-card__footer {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--bo-surface-2);
}

.location-card__btn {
  width: 100%;
  padding: 10px 16px;
  background: var(--bo-surface-2);
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--bo-ink);
  cursor: pointer;
  transition: background 0.2s ease;
}

.location-card__btn:hover {
  background: var(--bo-line);
}

.location-card--closed .location-card__btn {
  opacity: 0.6;
}
</style>
